import React, { useState, useEffect } from 'react';
import { editIcon } from '../../utils/images';
import { ProductWithTagNames } from '../../types/Product';
import './ProductCard.scss';
import { renameProduct, updateProductRecommendation, updateProductSale } from '../../services/productsList'

interface ProductCardProps {
  product: ProductWithTagNames;
  onGetAuthenticatedImage?: (imageUrl: string) => Promise<string>;
  onProductUpdate?: (productId: number) => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({ 
  product, 
  onGetAuthenticatedImage,
  onProductUpdate
}) => {
  const [baseImageUrl, setBaseImageUrl] = useState<string>('');
  const [aiImageUrl, setAiImageUrl] = useState<string>('');
  const [imageLoading, setImageLoading] = useState(true);
  const [name, setName] = useState(product.name);
  const [isEditing, setIsEditing] = useState(false);
  const [isRecommended, setIsRecommended] = useState(product.is_recommended || false);
  const [isOnSale, setIsOnSale] = useState(product.is_sale || false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const loadAuthenticatedImages = async () => {
      if (!onGetAuthenticatedImage) {
        setImageLoading(false);
        return;
      }

      try {
        setImageLoading(true);
        
        // Load base image (first one if available)
        if (product.base_image_url && product.base_image_url.length > 0) {
          const authenticatedBaseUrl = await onGetAuthenticatedImage(product.base_image_url[0]);
          setBaseImageUrl(authenticatedBaseUrl);
        }
        
        // Load AI image (first one if available)
        if (product.ai_image_url && product.ai_image_url.length > 0) {
          const authenticatedAiUrl = await onGetAuthenticatedImage(product.ai_image_url[0]);
          setAiImageUrl(authenticatedAiUrl);
        }
      } catch (error) {
        console.error('Error loading authenticated images:', error);
      } finally {
        setImageLoading(false);
      }
    };

    loadAuthenticatedImages();
  }, [product.base_image_url, product.ai_image_url, onGetAuthenticatedImage]);

  useEffect(() => {
    setName(product.name);
    setIsRecommended(product.is_recommended || false);
    setIsOnSale(product.is_sale || false);
  }, [product.name, product.is_recommended, product.is_sale]);

  const handleEditClick = () => {
    setIsEditing(true);
  };
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
	setName(e.target.value);
  };
  const handleSave = async () => {
	renameProduct(product.name,name);
	setIsEditing(false);
  };

  const handleRecommendedToggle = async (e: React.MouseEvent<HTMLButtonElement>): Promise<boolean> => {
    e.preventDefault();
    if (isUpdating) return false;
    
    try {
      setIsUpdating(true);
      const newRecommendedStatus = !isRecommended;
      const response = await updateProductRecommendation(product.id, newRecommendedStatus);
      setIsRecommended(response.is_marked);
      return true;
    } catch (error) {
      console.error('Error updating recommendation status:', error);
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaleToggle = async (e: React.MouseEvent<HTMLButtonElement>): Promise<boolean> => {
    e.preventDefault();
    if (isUpdating) return false;
    
    try {
      setIsUpdating(true);
      const newSaleStatus = !isOnSale;
      const response = await updateProductSale(product.id, newSaleStatus);
      setIsOnSale(response.is_marked);
      return true;
    } catch (error) {
      console.error('Error updating sale status:', error);
      return false;
    } finally {
      setIsUpdating(false);
    }
  };
  return (
    <div className="product-card">
      <div className="product-card__image">
        {imageLoading ? (
          <div className="product-card__image--loading">Loading...</div>
        ) : (
          <>
            {aiImageUrl && (
              <img src={aiImageUrl} alt="product AI" className="product-card__image--ai"/>
            )}
            <div className="product-card__image--product">
              {baseImageUrl && (
                <img src={baseImageUrl} alt={product.name} />
              )}
            </div>
            <div className="product-card__labels">
              {isRecommended && (
                <span className="product-card__label product-card__label--recommended">
                  Recommended
                </span>
              )}
              {isOnSale && (
                <span className="product-card__label product-card__label--sale">
                  Sale
                </span>
              )}
            </div>
          </>
        )}
      </div>
      <div className="product-card__wrapper">
        <div className="product-card__info">
		  {isEditing ? (<input type="text" value={name} onChange={handleNameChange} onBlur={handleSave} autoFocus/>) : (<span>{name}</span>)}
        </div>

        <div className="product-card__hashtags">
          {product.ai_tags.map((tag, index) => (
             <span key={index} className="product-card__hashtag">#{tag}</span>
          ))}
        </div>

        <div className="product-card__controls">
          <button 
            type="button"
            className={`product-card__control-btn ${isRecommended ? 'product-card__control-btn--active' : ''}`}
            onClick={handleRecommendedToggle}
            disabled={isUpdating}
          >
            {isRecommended ? 'Is' : 'Not'} Recommended
          </button>
          <button 
            type="button"
            className={`product-card__control-btn ${isOnSale ? 'product-card__control-btn--active' : ''}`}
            onClick={handleSaleToggle}
            disabled={isUpdating}
          >
            {isOnSale ? 'Is on' : 'Not on'} Sale
          </button>
        </div>

		<button onClick={handleEditClick}>
		  <img className="product-card__edit" src={editIcon} alt="edit button" />
		</button>
      </div>
    </div>
  );
};